/**
 * Helper per gestire i limiti di analisi AI per piano.
 *
 * I limiti sono salvati sul team doc Firestore:
 *  - aiAnalysesUsed: numero progressivo nel periodo corrente
 *  - aiAnalysesResetAt: Timestamp del prossimo reset (per piani paganti, mensile)
 *
 * Per il trial il counter è cumulativo (non si resetta finché non si converte
 * a un piano pagante).
 *
 * NOTA SICUREZZA: il check è client-side per il MVP. Un dev malizioso potrebbe
 * bypassarlo. Quando integriamo Stripe + Cloud Functions porteremo il check
 * server-side dentro race-engineer-api.
 */
import { doc, getDoc, updateDoc, increment, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';

// Limiti per piano. Allineati a docs/pricing-analysis.md v2.
const PLAN_LIMITS = {
  'Solo Driver': 30,
  'Mini-Team':   80,
  'Team':        250,
  'Pro Officina': 800,
  // Custom = illimitato (gestito da check `isUnlimited` sotto)
};

// Valori legacy di subscriptionPlan usati dai team creati prima del pricing v2.
// Trattati come TRIAL (limite 20 totali) per uniformità.
const TRIAL_PLAN_VALUES = ['Beta', 'trial', 'Trial'];

// Valori legacy che indicano un team ATTIVATO senza tier specifico assegnato
// (es. 'Attivo' impostato dallo SuperAdmin dopo conversione trial→pagante).
// Trattati come ILLIMITATI fino a quando non gli si assegna un piano preciso.
const LEGACY_ACTIVE_VALUES = ['Attivo', 'Active', 'Free'];

// Limite trial: cumulativo sui 14 giorni, NON mensile.
const TRIAL_LIMIT_TOTAL = 20;

/**
 * Restituisce le info quota AI per un team.
 * FAIL-SAFE: se il piano non è riconosciuto, il team è trattato come ILLIMITATO
 * (meglio permissivo che bloccare per errore un team che paga davvero).
 *
 * @param {object} team - documento Firestore team (con id)
 * @returns {{ limit, used, remaining, isUnlimited, isTrial, planLabel }}
 */
export function computeAiQuota(team) {
  if (!team) {
    return { limit: 0, used: 0, remaining: 0, isUnlimited: true, isTrial: false, planLabel: 'unknown' };
  }
  const plan = team.subscriptionPlan || 'Beta';
  const used = team.aiAnalysesUsed || 0;

  // 1. Custom = illimitato esplicito
  if (plan === 'Custom' || team.aiAnalysesLimit === -1) {
    return { limit: Infinity, used, remaining: Infinity, isUnlimited: true, isTrial: false, planLabel: plan };
  }

  // 2. Trial: Beta / trial / Trial / subscriptionPlan assente
  if (!team.subscriptionPlan || TRIAL_PLAN_VALUES.includes(plan)) {
    const limit = TRIAL_LIMIT_TOTAL;
    return {
      limit, used, remaining: Math.max(0, limit - used),
      isUnlimited: false, isTrial: true, planLabel: 'Trial',
    };
  }

  // 3. Piano pagante riconosciuto (nuovi nomi v2)
  if (typeof PLAN_LIMITS[plan] === 'number') {
    const limit = PLAN_LIMITS[plan];
    return {
      limit, used, remaining: Math.max(0, limit - used),
      isUnlimited: false, isTrial: false, planLabel: plan,
    };
  }

  // 4. Legacy "attivato senza tier" (Free/Attivo) → ILLIMITATO (fail-safe)
  if (LEGACY_ACTIVE_VALUES.includes(plan)) {
    return { limit: Infinity, used, remaining: Infinity, isUnlimited: true, isTrial: false, planLabel: plan };
  }

  // 5. Piano sconosciuto → ILLIMITATO (fail-safe permissivo)
  console.warn(`[aiQuota] Piano non riconosciuto: "${plan}". Trattato come illimitato per sicurezza.`);
  return { limit: Infinity, used, remaining: Infinity, isUnlimited: true, isTrial: false, planLabel: plan };
}

/**
 * Legge da Firestore il team e calcola la quota.
 * Esegue anche il reset mensile se necessario (solo per piani paganti).
 */
export async function fetchAiQuota(teamId) {
  if (!teamId) return null;
  const ref = doc(db, 'teams', teamId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const team = { id: snap.id, ...snap.data() };

  // Reset mensile automatico per piani paganti
  const plan = team.subscriptionPlan || 'Beta';
  const isPayingPlan = PLAN_LIMITS[plan] !== undefined;
  if (isPayingPlan && team.aiAnalysesResetAt) {
    const resetMs = team.aiAnalysesResetAt.toDate?.()?.getTime?.();
    if (resetMs && resetMs < Date.now()) {
      const nextReset = new Date();
      nextReset.setMonth(nextReset.getMonth() + 1);
      nextReset.setDate(1);
      nextReset.setHours(0, 0, 0, 0);
      try {
        await updateDoc(ref, {
          aiAnalysesUsed: 0,
          aiAnalysesResetAt: Timestamp.fromDate(nextReset),
        });
        team.aiAnalysesUsed = 0;
        team.aiAnalysesResetAt = Timestamp.fromDate(nextReset);
      } catch (e) {
        console.warn('Reset quota AI fallito (non blocking):', e);
      }
    }
  }

  return { ...computeAiQuota(team), team };
}

/**
 * Incrementa il counter analisi AI di +1. Da chiamare dopo una call API riuscita.
 */
export async function incrementAiUsage(teamId) {
  if (!teamId) return;
  const ref = doc(db, 'teams', teamId);
  try {
    await updateDoc(ref, {
      aiAnalysesUsed: increment(1),
      aiAnalysesLastUsedAt: serverTimestamp(),
    });
  } catch (e) {
    console.warn('Increment quota AI fallito (non blocking):', e);
  }
}
